import { type RefObject, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { CircleDashed, Eye, EyeOff, RotateCcw, Scissors, SquareCenterlineDashedHorizontal } from 'lucide-react';
import { useAtlas, type AtlasState, type LabelDensity, type StrokeState } from '../app/store.ts';
import { leavesOut, scenarioById } from '../scenarios/index.ts';
import { drawnOverlays, isLive, liveActive, mainSlot, offeredPresets, slotsOf, stillView, runsAside, viewOnAnatomy } from '../app/scene.ts';
import { isConnectome } from '../data/tissue.ts';
import { systemSwatch } from '../data/catalogue.ts';
import { layerModes } from '../viewport/bridge.ts';
import type { Mode, SectionPlane } from '../viewport/api.ts';
import { Button, Checkbox, IconButton, IconSegmented, Section, Segmented, Slider, Switch, Tip, type SegmentOption } from '../ui/primitives.tsx';
import { liveSolver } from '../app/live.ts';
import { armsFor, clotsNow, fixedGrade, gradeOf, islesView, partOf, reopensAt, siteOf, strokeChoices, tissueAt, type CollateralGrade } from '../app/stroke.ts';
import { openedSlot } from '../app/sims.ts';
import { flowChanges } from '../data/sims/live.ts';
import { loadIsles } from '../data/sims/isles.ts';
import { defaultSource } from '../data/sims/files.ts';
import { useLoaded } from '../ui/hooks.ts';
import { formatTime } from './time.ts';
import { formatValue } from '../ui/chart.ts';
import { colormapStops, gradientCss, onTissue, RAMPS } from '../ui/scales.ts';
import { siteKinds, type SiteKind } from './ElectrodeSites.tsx';
import { modalityOf, patientOf } from '../data/sims/comparison.ts';
import type { DynamicsHeader, PharmaHeader } from '../data/sims/types.ts';
import { courseDose } from '../data/sims/dose.ts';
import { duration } from '../data/sims/time.ts';
import { usePharmaCourse } from './usePharmaCourse.ts';
import { valueAt } from '../ui/chart.ts';
import { Sparkline } from '../ui/Sparkline.tsx';
import { TARGET_LABEL } from './Traces.tsx';
import { isCircuitKind, loadCircuit } from '../data/sims/circuit.ts';
import { useDrugDensity, useDrugDensityDrive, type DrugShow } from './drugDensity.ts';
import type { CellsSim } from '../data/sims/cells.ts';
import { formatCount } from '../data/measures.ts';
import { StimSections } from './Stimulate.tsx';
import { MindSections } from './MindLive.tsx';
import { JourneySections } from './Journey.tsx';
import { SceneTourLink } from './Tour.tsx';
import { SheetHandle } from './SheetHandle.tsx';
import { PcomOutcome } from './PcomOutcome.tsx';
import { DfcSections } from './Dfc.tsx';
import { createPortal } from 'react-dom';
import { DrugCompare } from './DrugCompare.tsx';
import { DrugMaps } from './DrugMaps.tsx';
import { drugOptions, openCompare, useCompare } from './compare.ts';
import { paintMaps, useDrugMaps } from './drugMaps.ts';
import { TextSections } from './Text.tsx';
import { CfdSections } from './Cfd.tsx';
import { LifetimeSections } from './Lifetime.tsx';
import type { LucideIcon } from 'lucide-react';

// Spec §5: show · ghost · hide as eye, circle-dashed, eye-off.
const MODE_OPTIONS: (SegmentOption<Mode> & { icon: LucideIcon })[] = [
  { value: 'shown', label: 'Show', icon: Eye },
  { value: 'ghost', label: 'Ghost', icon: CircleDashed },
  { value: 'hidden', label: 'Hide', icon: EyeOff },
];

/**
 * Whether the panel would scroll with every view listed. `others` is what the other views' rows take
 * (measured while they're listed), so a collapsed list knows whether listing them again would fit.
 */
function useCrowded(scroll: RefObject<HTMLElement | null>, list: RefObject<HTMLElement | null>, collapsedNow: RefObject<boolean>, active: number): boolean {
  const [crowded, setCrowded] = useState(false);
  const others = useRef(0);
  useLayoutEffect(() => {
    const el = scroll.current;
    const ul = list.current;
    if (!el || !ul) return;
    const measure = () => {
      const collapsed = collapsedNow.current;
      if (!collapsed) others.current = ul.offsetHeight - ((ul.children[active] as HTMLElement | undefined)?.offsetHeight ?? 0);
      const last = el.lastElementChild as HTMLElement | null;
      const pad = parseFloat(getComputedStyle(el).paddingBottom) || 0;
      const content = last ? last.getBoundingClientRect().bottom - el.getBoundingClientRect().top + el.scrollTop + pad : 0;
      setCrowded(content + (collapsed ? others.current : 0) - el.clientHeight > 0.5);
    };
    measure();
    // The panel's height (the window) and its sections' (a catalogue loading, the Stroke controls) both count.
    const ro = new ResizeObserver(measure);
    const watch = () => {
      ro.disconnect();
      ro.observe(el);
      for (const c of Array.from(el.children)) ro.observe(c);
    };
    watch();
    const mo = new MutationObserver(() => {
      watch();
      measure();
    });
    mo.observe(el, { childList: true });
    return () => {
      ro.disconnect();
      mo.disconnect();
    };
  });
  return crowded;
}

function Presets({ scroll }: { scroll: RefObject<HTMLElement | null> }) {
  const scenario = useAtlas((s) => scenarioById(s.scenario));
  const packs = useAtlas((s) => s.engine.packs);
  const journeys = useAtlas((s) => s.engine.journeys);
  const layers = useAtlas((s) => s.engine.layers);
  const kind = useAtlas((s) => s.engine.kind);
  const preset = useAtlas((s) => s.preset);
  const applyPreset = useAtlas((s) => s.applyPreset);
  // Spec §5: a scene that needs more room collapses Views to its active row: a section plane's
  // position row, or a scene whose panel must fit (Explore, spec §1) whenever it would otherwise scroll.
  const sectionOn = useAtlas((s) => s.section.length > 0);
  const [expanded, setExpanded] = useState(false);
  const listRef = useRef<HTMLUListElement>(null);
  // A view on a tissue pack only where the viewport loads one.
  const presets = offeredPresets(scenario, { packs, journeys, layers, kind });
  const active = presets.findIndex((p) => p.id === preset);
  // Read by the measurement after this render, which is when it runs.
  const collapsedNow = useRef(false);
  const crowded = useCrowded(scroll, listRef, collapsedNow, active);
  // Collapsed, Views lists only its active row, or none once a change (moving the section) has left
  // every view; the head's All views lists them again, to choose from, and the choice closes the list
  // back to its rule: a view picked from the open list must not leave the panel scrolling (Explore
  // with a seventh view, Powers of Ten, and the section's Position row ran 37 px over; ba-engine, 10-03).
  const collapsed = (sectionOn || (!!scenario.fit && crowded)) && !expanded;
  collapsedNow.current = collapsed;
  const rows = collapsed ? presets.filter((p) => p.id === preset) : presets;
  const pick = (p: (typeof presets)[number]) => {
    applyPreset(p);
    setExpanded(false);
  };
  return (
    <Section
      title="Views"
      action={
        sectionOn || (!!scenario.fit && crowded) ? (
          <button type="button" className="link-btn ba-type-small" onClick={() => setExpanded(!expanded)} aria-expanded={!collapsed}>
            {collapsed ? 'All views' : 'Fewer'}
          </button>
        ) : undefined
      }
    >
      <ul ref={listRef} className="presets" role="radiogroup" aria-label="Views">
        {rows.map((p) => {
          const on = preset === p.id;
          const row = (
            <button type="button" role="radio" aria-checked={on} className={`preset ${on ? 'on' : ''}`} onClick={() => pick(p)}>
              <span className="preset-dot" aria-hidden="true" />
              <span className="preset-text">
                <span className="preset-name ba-type-body-strong">{p.name}</span>
                {on ? <span className="preset-hint ba-type-caption">{p.hint}</span> : null}
              </span>
            </button>
          );
          return (
            <li key={p.id}>
              {on ? (
                row
              ) : (
                <Tip label={p.hint} side="right">
                  {row}
                </Tip>
              )}
            </li>
          );
        })}
      </ul>
    </Section>
  );
}

function Layers() {
  const catalogue = useAtlas((s) => s.catalogue);
  const layers = useAtlas((s) => s.layers);
  const setLayer = useAtlas((s) => s.setLayer);
  const isolate = useAtlas((s) => s.isolate);
  const isolateTarget = useAtlas((s) => s.isolateTarget);
  const showAll = useAtlas((s) => s.showAll);
  const anyHidden = useAtlas((s) => Object.values(s.overrides).includes('hidden'));
  const sc = useAtlas((s) => scenarioById(s.scenario));
  const anatomyView = useAtlas(viewOnAnatomy);
  if (!catalogue) return null;
  const modes = layerModes({ catalogue, layers });
  // The systems the scene lists: one it leaves out (Explore's veins and sinuses, all but Blood flow's three) has no row. A view
  // on its own anatomy pack (Lifetime's Parkinson's stages on the MNI template) lists that pack's systems.
  const systems = anatomyView ? catalogue.systems : catalogue.systems.filter((g) => !leavesOut(sc, g.system));
  const isolatedSystem = isolate && 'system' in isolate ? isolate.system : null;
  // A connectome pack's layers are drawn or not: the viewport has no ghost for them, and doesn't isolate.
  const wiring = isConnectome(catalogue);
  const options = wiring ? MODE_OPTIONS.filter((o) => o.value !== 'ghost') : MODE_OPTIONS;
  // Spec §5: the head offers "Show all" while anything is isolated or hidden.
  const narrowed = !!isolate || anyHidden || systems.some((g) => modes[g.system] === 'hidden');
  return (
    <Section
      title="Layers"
      action={
        narrowed ? (
          <button type="button" className="link-btn ba-type-small" onClick={showAll}>
            Show all
          </button>
        ) : null
      }
    >
      <ul className="layers">
        {systems.map((g) => {
          const swatch = systemSwatch(g.system, g.members[0]?.color);
          const mode = modes[g.system] ?? 'shown';
          return (
            <li key={g.system} className={`layer ${mode} ${isolatedSystem === g.system ? 'isolated' : ''}`}>
              {wiring ? (
                <span className="layer-name">
                  <span className="swatch" style={{ background: swatch }} aria-hidden="true" />
                  <span className="layer-label ba-type-body">{g.label}</span>
                  <span className="layer-count ba-type-caption num">{g.members.length}</span>
                </span>
              ) : (
                <button type="button" className="layer-name" title={`Isolate ${g.label.toLowerCase()}`} onClick={() => isolateTarget(isolatedSystem === g.system ? null : { system: g.system })}>
                  <span className="swatch" style={{ background: swatch }} aria-hidden="true" />
                  <span className="layer-label ba-type-body">{g.label}</span>
                  <span className="layer-count ba-type-caption num">{g.members.length}</span>
                </button>
              )}
              <IconSegmented label={`${g.label} visibility`} value={mode === 'ghost' && wiring ? 'shown' : mode} options={options} onChange={(m) => setLayer(g.system, m)} />
            </li>
          );
        })}
      </ul>
    </Section>
  );
}

/** The scene's choices' rows: which run it shows (a model, a drug). The ones the Stroke slice fills
 *  (collaterals, thrombectomy) have their controls with the clot's. Null for none. */
function useChoiceRows() {
  const scenario = useAtlas((s) => scenarioById(s.scenario));
  const options = useAtlas((s) => (s.sims.scenario === s.scenario ? s.sims.options : null));
  const resolved = useAtlas((s) => (s.sims.scenario === s.scenario ? s.sims.values : undefined));
  const choices = useAtlas((s) => s.choices);
  const setChoice = useAtlas((s) => s.setChoice);
  const specs = (scenario.choices ?? []).filter((c) => !c.fromSlice && !c.own && options?.[c.id]?.some((o) => o.available));
  if (!specs.length || !options) return null;
  return (
    <div className="rows choices">
      {specs.map((c) => {
        const opts = options[c.id]!;
        // The user's pick while it has a run; else what the scene resolved to (the nearest that has one).
        const picked = choices[c.id];
        const value = (picked && opts.find((o) => o.value === picked)?.available ? picked : undefined) ?? resolved?.[c.id] ?? opts.find((o) => o.value === c.default || (o as { id?: string }).id === c.default)?.value ?? opts[0]?.value ?? '';
        return (
          <div key={c.id} className="row choice-row">
            <span>{c.label}</span>
            {opts.length > 4 ? (
              <select className="select" aria-label={c.label} value={value} onChange={(e) => setChoice(c.id, e.target.value)}>
                {opts.map((o) => (
                  <option key={o.value} value={o.value} disabled={!o.available}>
                    {o.label}
                  </option>
                ))}
              </select>
            ) : (
              <Segmented
                size="sm"
                label={c.label}
                value={value}
                options={opts.filter((o) => o.available).map((o) => ({ value: o.value, label: o.label, ...(o.hint ? { hint: o.hint } : {}) }))}
                onChange={(v) => setChoice(c.id, v)}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

/** A basilar clot's PCom anatomy (ba-blood's v5 pair): the diameters go in the hint and in Methods. */
const PCOM: { value: 'segmented' | 'traced'; label: string; hint: string }[] = [
  { value: 'segmented', label: 'as segmented', hint: 'As segmented from the TOF-MRA: left PCom 0.3 mm, right absent' },
  { value: 'traced', label: 'traced', hint: 'Traced on the TOF-MRA: left PCom 0.70 mm, right 0.667–0.70 mm' },
];

const THROMBECTOMY: { value: string; label: string; hours: number | null }[] = [
  { value: 'none', label: 'None', hours: null },
  { value: '1', label: '1 h', hours: 1 },
  { value: '3', label: '3 h', hours: 3 },
  { value: '6', label: '6 h', hours: 6 },
];

const COLOUR_BY: SegmentOption<StrokeState['colourBy']>[] = [
  { value: 'velocity', label: 'Velocity' },
  { value: 'pressure', label: 'Pressure' },
  { value: 'none', label: 'None' },
];

/** The Previous model's collateral grades, hemo's three tissue-fate arms per clot (app/stroke.ts gradeOf). */
const GRADES: SegmentOption<CollateralGrade>[] = [
  { value: 'good', label: 'Good', hint: 'Good collaterals' },
  { value: 'intermediate', label: 'Fair', hint: 'Intermediate collateral grade' },
  { value: 'poor', label: 'Poor', hint: 'Poor collaterals: the circle’s ACom and PComs out of the live solve too' },
];

/** The clot's rows, when the scene has a live solver loaded: which vessel and how far along it, placed
 *  by a click too, and in Stroke the collaterals, the thrombectomy and Compare with ISLES'24 (spec §15's
 *  Scenario rows). Null without a solver. */
function useClotRows() {
  const live = useAtlas((s) => s.live);
  const stroke = useAtlas((s) => s.stroke);
  const clinical = useAtlas((s) => !!scenarioById(s.scenario).live?.clinical);
  const reopen = useAtlas(reopensAt);
  const reopened = useAtlas((s) => !!s.stroke.occlusion && clotsNow(s).length === 0);
  const setLive = useAtlas((s) => s.setLive);
  const setOcclusion = useAtlas((s) => s.setOcclusion);
  const setClotPosition = useAtlas((s) => s.setClotPosition);
  const setCollaterals = useAtlas((s) => s.setCollaterals);
  const setCollateralGrade = useAtlas((s) => s.setCollateralGrade);
  const setPcom = useAtlas((s) => s.setPcom);
  const setThrombectomy = useAtlas((s) => s.setThrombectomy);
  const setCompare = useAtlas((s) => s.setCompare);
  // A model whose tissue runs don't vary with the grade (v5) fixes it: the switch is the live solve's only. One whose runs do
  // (the Previous model, hemo's v4) gets the three grades instead.
  const gradeFixed = useAtlas((s) => !!fixedGrade(s) || (s.sims.scenario === s.scenario ? !!s.sims.fixed?.grade : false));
  const model = useAtlas((s) => (s.sims.scenario === s.scenario ? s.sims.values?.model : undefined));
  const solver = liveSolver();
  if (live.status !== 'ready' || !solver) return null;
  const o = stroke.occlusion;
  return (
    <div className="rows live">
      <div className="row">
        <span>Occlusion</span>
        <select className="select" aria-label="Occlusion" value={o?.vessel ?? ''} onChange={(e) => setOcclusion(e.target.value ? { vessel: e.target.value } : null)}>
          <option value="">None</option>
          {solver.vessels.map((v) => (
            <option key={v.id} value={v.id}>
              {v.name}
            </option>
          ))}
        </select>
      </div>
      {o ? (
        <div className="row live-position">
          <span>Position</span>
          <Slider label="Clot position along the vessel" min={0.1} max={0.9} step={0.01} value={o.s} onChange={setClotPosition} />
          <span className="num">{`${Math.round(o.s * 100)} %`}</span>
        </div>
      ) : null}
      {o && clinical && o.vessel === 'ba:left-mca-m1' ? (
        <p className="live-hint live-part">{partOf(o) ? 'Beyond the lenticulostriate arteries: distal M1' : 'Before the lenticulostriate arteries: proximal M1'}</p>
      ) : null}
      <label className="row">
        <span>Place by click</span>
        <Switch checked={live.tool} onChange={(tool) => setLive({ tool, missed: false })} label="Place the clot by clicking an artery" />
      </label>
      {live.tool && live.missed ? <p className="live-hint live-miss">Click an artery of the circle to place the clot</p> : null}
      {clinical ? (
        <>
          {gradeFixed ? (
            <>
              <label className="row">
                <span>Collaterals</span>
                <Switch checked={stroke.collaterals} onChange={setCollaterals} label="Collaterals" />
              </label>
              <p className="live-hint live-grade">{`${model ?? 'This model'}: the switch changes the live flow, not tissue fate`}</p>
            </>
          ) : (
            <div className="row">
              <span>Collaterals</span>
              <Segmented size="sm" label="Collaterals" value={gradeOf(stroke.collaterals, stroke.fair)} options={GRADES} onChange={setCollateralGrade} />
            </div>
          )}
          {siteOf(o?.vessel) === 'basilar' ? (
            <>
              <div className="row">
                <span>PCom</span>
                <Segmented size="sm" label="PCom anatomy" value={stroke.pcom} options={PCOM} onChange={setPcom} />
              </div>
              <PcomOutcome />
              {/* The live solve is hemo-rt's own 22-vessel network, its two PComs alike: the anatomy is the runs'. */}
              <p className="live-hint live-pcom">The switch changes the runs, not the live flow</p>
            </>
          ) : null}
          <div className="row">
            <span>Thrombectomy</span>
            <Segmented
              size="sm"
              label="Thrombectomy"
              value={THROMBECTOMY.find((t) => t.hours === stroke.thrombectomyH)?.value ?? 'none'}
              options={THROMBECTOMY.filter((t) => armsFor(siteOf(o?.vessel)).includes(t.hours)).map((t) => ({ value: t.value, label: t.label }))}
              onChange={(v) => setThrombectomy(THROMBECTOMY.find((t) => t.value === v)?.hours ?? null)}
            />
          </div>
          {/* Spec §15: a default button; primary while the comparison is on. */}
          <Button className="compare" variant={stroke.compare ? 'primary' : 'default'} aria-pressed={stroke.compare} onClick={() => setCompare(!stroke.compare)}>
            Compare with ISLES’24
          </Button>
        </>
      ) : null}
      {reopened && reopen !== null ? <p className="live-hint">{`Reopened at ${formatTime(reopen, 'h')}`}</p> : null}
      {live.note ? <p className="live-note">{live.note}</p> : null}
      {clinical && stroke.compare ? <IslesRows /> : null}
    </div>
  );
}

/** Spec §15's Scenario section: the scene's choices, then the clot where a live solver is loaded, the
 *  solve's time at the head's right. */
/**
 * Drugs' Dose and Route (spec §15's Scenario rows). The Dose is a slider, from a tenth to twice the
 * course's own, where the course follows the model that rescales it (data/sims/dose.ts): its PK is
 * linear in the dose, occupancy is C / (C + Ki) per target, and the cortex scales by the occupancy
 * ratio. Otherwise it reads the course's own dose. The route is the course's: its bioavailability isn't
 * identified, so there's no IV course to switch to.
 */
function useDoseRows() {
  const header = useAtlas((s) => (s.sims.scenario === s.scenario ? s.sims.slots.find((x) => x.slot === 'main' && x.format === 'pharma')?.header : undefined)) as PharmaHeader | undefined;
  const drug = useAtlas((s) => s.drug);
  const drugDose = useAtlas((s) => s.drugDose);
  const setDrugDose = useAtlas((s) => s.setDrugDose);
  const dose = courseDose(header);
  if (!dose) return null;
  const live = !!header && drug?.run === header.id && drug.fits;
  const value = live && drugDose !== null ? drugDose : dose.mass;
  const label = (m: number) => `${formatValue(m)}${dose.unit ? ` ${dose.unit}` : ''}`;
  return (
    <div className="rows dose">
      {live ? (
        <div className="row slider-row dose-row">
          <span className="slider-lead">Dose</span>
          <Slider label="Dose" min={dose.mass / 10} max={dose.mass * 2} step={dose.mass / 40} value={value} onChange={setDrugDose} />
          <span className="slider-value num">{label(value)}</span>
        </div>
      ) : (
        <div className="row">
          <span>Dose</span>
          <span className="num">{label(dose.mass)}</span>
        </div>
      )}
      {dose.route ? (
        <div className="row">
          <span>Route</span>
          <span>{dose.route[0]!.toUpperCase() + dose.route.slice(1)}</span>
        </div>
      ) : null}
      {value !== dose.mass ? (
        <p className="live-hint">
          {`The course was run at ${label(dose.mass)}; this is it at ${label(value)} on its own model. `}
          <button type="button" className="link-btn" onClick={() => setDrugDose(null)}>
            Back to {label(dose.mass)}
          </button>
        </p>
      ) : null}
    </div>
  );
}

function Scenario() {
  const live = useAtlas((s) => s.live);
  const choices = useChoiceRows();
  const clot = useClotRows();
  const dose = useDoseRows();
  // The measured ΔFC view (features/Dfc.tsx) picks its own drug and condition; the course's choices and dose stand aside.
  const still = useAtlas(stillView);
  if (still || (!choices && !clot && !dose)) return null;
  const solver = live.status === 'ready' ? liveSolver() : null;
  const r = solver ? live.result : null;
  return (
    <Section title="Scenario" action={r && solver ? <span className="live-time num">{`${solver.id} · ${r.computeMs.toFixed(1)} ms`}</span> : null}>
      {choices}
      {dose}
      {clot}
    </Section>
  );
}

/** Compare with ISLES'24: the real patients scored against the run on screen, the model's infarct
 *  beside the two trivial predictions it has to beat, no infarct and the admission core. Medians over
 *  the matched patients: the volume error and the Dice of each, the best of the three in each column
 *  set apart, and the measured infarct under them. Methods says how they were matched and scored. */
function IslesRows() {
  const paths = useAtlas((s) => scenarioById(s.scenario).live?.isles);
  const exportScored = useAtlas((s) => scenarioById(s.scenario).live?.islesModel);
  const site = useAtlas((s) => strokeChoices(s).site ?? '');
  const part = useAtlas((s) => strokeChoices(s).part ?? '');
  const grade = useAtlas((s) => strokeChoices(s).grade ?? '');
  const treatment = useAtlas((s) => strokeChoices(s).treatment ?? '');
  const model = useAtlas((s) => (s.sims.scenario === s.scenario ? s.sims.values?.model : undefined));
  const cmp = useLoaded(() => (paths?.length ? loadIsles(defaultSource(), paths) : null), [paths]);
  const view = islesView({ site, part, grade, treatment }, model, cmp.value ?? null, exportScored);
  if (!view) {
    if (!cmp.done) return null;
    if (cmp.error) return <p className="live-note">{cmp.error}</p>;
    if (!site) return <p className="live-hint">ISLES’24 patients were matched to left M1 and ICA clots only.</p>;
    if (!cmp.value) return <p className="live-hint">No ISLES’24 comparison on this server for this run.</p>;
    return <p className="live-hint">No ISLES’24 patient matches this run.</p>;
  }
  const { scores, scored } = view;
  const against = model && scored !== model ? scored : null;
  const rows = [
    { key: 'model', label: `${scored} model`, s: scores.model },
    { key: 'zero', label: 'No infarct', s: scores.zero },
    { key: 'core', label: 'Admission core', s: scores.core },
  ] as const;
  const fin = (xs: number[]) => xs.filter(Number.isFinite);
  const bestErr = Math.min(...fin(rows.map((r) => r.s.errMl)));
  const bestDice = Math.max(...fin(rows.map((r) => r.s.dice)));
  const fixed = (v: number) => (Number.isFinite(v) ? v.toFixed(2) : '–');
  return (
    <div className="live-changes isles">
      <span className="caps">{`ISLES’24${against ? ` against ${against}` : ''}, ${scores.n} patient${scores.n === 1 ? '' : 's'}, median`}</span>
      <div className="isles-table" role="table" aria-label="ISLES’24: the model beside no infarct and the admission core">
        <div className="isles-row isles-head" role="row">
          <span role="columnheader">Prediction</span>
          <span role="columnheader">Error, mL</span>
          <span role="columnheader">Dice</span>
        </div>
        {rows.map((r) => (
          <div key={r.key} className="isles-row" role="row" data-row={r.key}>
            <span role="rowheader">{r.label}</span>
            <span role="cell" className={`num${r.s.errMl === bestErr ? ' best' : ''}`}>
              {fixed(r.s.errMl)}
            </span>
            <span role="cell" className={`num${r.s.dice === bestDice ? ' best' : ''}`}>
              {fixed(r.s.dice)}
            </span>
          </div>
        ))}
      </div>
      <div className="live-change">
        <span>Measured infarct, mL</span>
        <span className="num">{fixed(scores.measuredMl)}</span>
      </div>
    </div>
  );
}

/** The tissue-fate run's states at the scene time (spec §15's Tissue state: a swatch row each, with its
 *  volume), or why there are none. */
function TissueStates() {
  const site = useAtlas((s) => siteOf(s.stroke.occlusion?.vessel));
  const main = useAtlas((s) => (s.sims.scenario === s.scenario ? s.sims.slots.find((x) => x.slot === 'main') : undefined));
  // The run's rows are 10 minutes apart: a minute of scene time is fine enough.
  const minute = useAtlas((s) => Math.floor(s.time / 60));
  const o = main ? openedSlot('main') : undefined;
  const sim = o?.format === 'hemo' ? o.sim : null;
  const table = useLoaded(() => (sim ? sim.growth() : null), [sim]);
  const ready = useAtlas((s) => s.sims.scenario === s.scenario && s.sims.status === 'ready');
  if (!site) return <p className="live-hint">No tissue-fate run for a clot in this vessel.</p>;
  if (!table.value) {
    // The scene's runs are in, and none is a tissue-fate run for this clot and arm (hemo ran its
    // distal M1 clot untreated and reperfused at 3 h only).
    return ready && table.done && main?.header.kind !== 'tissue-fate' ? <p className="live-hint">No tissue-fate run for this clot and thrombectomy time.</p> : null;
  }
  return (
    <div className="rows tissue-states" role="list" aria-label="Tissue state">
      <span className="row tissue-head">Tissue state</span>
      {tissueAt(table.value, minute * 60).map((t) => (
        <div key={t.col} className="row tissue-state" role="listitem" data-state={t.col}>
          <span className="tissue-name">
            <span className="swatch" style={{ background: `var(--ba-scale-tissue-fate-${t.token})` }} aria-hidden="true" />
            {t.label}
          </span>
          <span className="num">{`${formatValue(t.ml)} mL`}</span>
        </div>
      ))}
    </div>
  );
}

/** Spec §15's Tissue section (Stroke): the tissue states and their volumes over time, and the spreading
 *  depolarizations' switch where the scene has the run. The perfusion deficit's switch and colour bar
 *  wait for the viewport to draw the run's volumes (its `files.volumes`). */
function Tissue() {
  const clinical = useAtlas((s) => !!scenarioById(s.scenario).live?.clinical);
  const sd = useAtlas((s) => (s.sims.scenario === s.scenario ? s.sims.slots.some((x) => x.slot === 'sd' && x.format === 'dynamics') : false));
  const on = useAtlas((s) => s.toggles.activity);
  const setToggle = useAtlas((s) => s.setToggle);
  if (!clinical) return null;
  return (
    <Section title="Tissue">
      <TissueStates />
      {sd ? (
        <div className="rows">
          <label className="row">
            <span>Spreading depolarizations</span>
            <Switch checked={on} onChange={(v) => setToggle('activity', v)} label="Spreading depolarizations" />
          </label>
        </div>
      ) : null}
    </Section>
  );
}

const QUANTITY_NAME: Record<string, string> = { velocity: 'Velocity', pressure: 'Pressure' };

/** The overlay the arteries are coloured by: the live solve's while it drives the picture, else the runs'. */
function vesselOverlay(s: Parameters<typeof liveActive>[0] & Parameters<typeof drawnOverlays>[0]) {
  if (liveActive(s)) return s.live.overlay;
  return drawnOverlays(s).find((o) => o.ramp === 'velocity' || o.ramp === 'pressure') ?? null;
}

/** Spec §15's Vessels section (Blood flow, Stroke): what the arteries are coloured by, with its colour
 *  bar, and while a clot is in, the vessels whose flow it changed most. */
function Vessels() {
  const colourBy = useAtlas((s) => s.stroke.colourBy);
  const setColourBy = useAtlas((s) => s.setColourBy);
  const overlay = useAtlas(vesselOverlay);
  const live = useAtlas((s) => s.live);
  const network = useAtlas((s) => slotsOf(s).some((x) => x.format === 'hemo' && x.header.kind === '1d-network'));
  // The 1D runs stand aside in an aside view (3D flow draws its own run): their colour section with them.
  const aside = useAtlas(runsAside);
  const solver = live.status === 'ready' ? liveSolver() : null;
  if ((!network && !solver) || aside) return null;
  const r = live.result;
  const changes = solver && r && (r.clots.length || r.absent.length) ? flowChanges(solver, r) : { gained: [], lost: [] };
  const rows = (title: string, list: typeof changes.gained) =>
    list.length ? (
      <div className="live-changes">
        <span className="caps">{title}</span>
        {list.map((c) => (
          <div key={c.id} className="live-change">
            <span>{c.reversed ? `${c.name}, reversed` : c.name}</span>
            <span className="num">{`${formatValue(c.before)} → ${formatValue(c.after)}`}</span>
          </div>
        ))}
      </div>
    ) : null;
  const shown = colourBy !== 'none' && overlay && overlay.quantity === colourBy ? overlay : null;
  return (
    <Section title="Vessels">
      <div className="rows">
        <div className="row">
          <span>Colour by</span>
          <Segmented size="sm" label="Colour the vessels by" value={colourBy} options={COLOUR_BY} onChange={setColourBy} />
        </div>
      </div>
      {shown ? <ColourBar name={QUANTITY_NAME[shown.quantity] ?? shown.quantity} scale={shown.ramp} min={`${formatValue(shown.range[0])} ${shown.units}`} max={`${formatValue(shown.range[1])} ${shown.units}`} /> : null}
      {solver ? rows(`More flow, ${solver.units.flow}`, changes.gained) : null}
      {solver ? rows(`Less flow, ${solver.units.flow}`, changes.lost) : null}
    </Section>
  );
}

/** Spec §15's Particles section (Blood flow): the 1D runs' particles on or off, and how many. Size waits
 *  for the viewport to take one. */
function Particles() {
  const clinical = useAtlas((s) => !!scenarioById(s.scenario).live?.clinical);
  const network = useAtlas((s) => slotsOf(s).some((x) => x.format === 'hemo' && x.header.kind === '1d-network' && !x.appOnly));
  const particles = useAtlas((s) => s.particles);
  const setParticles = useAtlas((s) => s.setParticles);
  const aside = useAtlas(runsAside);
  if (clinical || !network || aside) return null;
  return (
    <Section title="Particles">
      <div className="rows">
        <label className="row">
          <span>Particles</span>
          <Switch checked={particles.on} onChange={(on) => setParticles({ on })} label="Particles" />
        </label>
        {particles.on ? (
          <div className="row live-position">
            <span>Density</span>
            <Slider label="Particle density" min={0.05} max={1} step={0.05} value={particles.density} onChange={(density) => setParticles({ density })} />
            <span className="num">{`${Math.round(particles.density * 100)} %`}</span>
          </div>
        ) : null}
      </div>
    </Section>
  );
}

/** What the cortex shows for the picked receptor (features/drugDensity.ts): the course's engagement, or the density alone. */
const SHOW_OPTIONS: SegmentOption<DrugShow>[] = [
  { value: 'engagement', label: 'Engagement', hint: 'Occupancy × density, the course’s tint' },
  { value: 'density', label: 'Density', hint: 'The receptor’s density alone: Hansen 2022’s PET parcel means on Schaefer-400' },
  { value: 'pet', label: 'PET', hint: 'The vertex-wise group PET map, where the pack has one' },
];

const LEVELS: { quantity: string; label: string }[] = [
  { quantity: 'plasma_total_nm', label: 'Blood' },
  { quantity: 'brain_unbound_nm', label: 'Brain' },
];

/**
 * Drugs' sections of spec §15, from the run's PK course.
 * - Levels: the concentration in the blood (plasma, total) and the brain (unbound), each a sparkline
 *   over the course with the scene time marked, and its value now.
 * - Receptors: each modelled target's occupancy now (the course's central estimate, or a snapshot's
 *   prediction), the primary target first. The one picked is what the traces chart and the cortex
 *   shows, with its colour bar. A run maps some targets on the cortex only (LSD v1: 5-HT2A), and the
 *   section says so for the others.
 */
function DrugSections() {
  const { sim, course, times, occupancy } = usePharmaCourse();
  const compareOpen = useCompare((s) => s.open);
  const setCompareOpen = useCompare((s) => s.setOpen);
  const closeCompare = useCallback(() => setCompareOpen(false), [setCompareOpen]);
  // The table reads the Drug list's headers when the sheet opens (once per list); a `compare=1` link opens it before the list
  // resolves, so the read follows the list too.
  const listKey = useAtlas((s) => (s.sims.scenario === s.scenario ? (s.sims.options.drug ?? []).map((o) => o.value).join(' ') : ''));
  useEffect(() => {
    if (compareOpen && listKey) void openCompare(drugOptions());
  }, [compareOpen, listKey]);
  // All maps (features/drugMaps.ts): the tiles paint when the sheet opens, once per Drug list and drawn cortex, after the
  // scene and its cortex are in (a `maps=1` link opens it first).
  const mapsOpen = useDrugMaps((s) => s.open);
  const setMapsOpen = useDrugMaps((s) => s.setOpen);
  const closeMaps = useCallback(() => setMapsOpen(false), [setMapsOpen]);
  const brainPack = useAtlas((s) => s.brain?.pack ?? null);
  const live = useAtlas((s) => s.engine.status === 'ready' && s.load.status === 'ready' && s.sims.scenario === s.scenario && s.sims.status === 'ready');
  useEffect(() => {
    if (mapsOpen && listKey && live) void paintMaps(drugOptions(), brainPack);
  }, [mapsOpen, listKey, live, brainPack]);
  const minute = useAtlas((s) => Math.floor(s.time / 60));
  const wanted = useAtlas((s) => s.traceView.target);
  const setTraceView = useAtlas((s) => s.setTraceView);
  const engagement = useAtlas((s) => drawnOverlays(s).find((o) => o.quantity === 'engagement') ?? null);
  // The measured ΔFC view (ba-web's Dfc.tsx) has its own sections; the course's stand aside with the clock.
  const still = useAtlas(stillView);
  // The Show control's map (features/drugDensity.ts), driven for the receptor the section shows.
  const show = useDrugDensity((s) => s.show);
  const setShow = useDrugDensity((s) => s.setShow);
  const mapStatus = useDrugDensity((s) => s.status);
  const mapWhy = useDrugDensity((s) => s.why);
  const map = useDrugDensity((s) => s.map);
  const shownTarget = sim && !still ? (wanted && sim.targets.includes(wanted) ? wanted : (sim.primaryTarget ?? [...sim.targets][0] ?? null)) : null;
  useDrugDensityDrive(shownTarget);
  if (!sim || still) return null;
  const end = duration(sim.axis);
  const t = times ? Math.min(Math.max(minute * 60, times[0]!), times[times.length - 1]!) : 0;
  const levels = course && times ? LEVELS.flatMap((l) => course.concentrations.filter((c) => c.quantity === l.quantity).map((c) => ({ ...l, c }))) : [];
  const primary = sim.primaryTarget;
  const targets = [...sim.targets].sort((a, b) => Number(b === primary) - Number(a === primary));
  const target = wanted && sim.targets.includes(wanted) ? wanted : (primary ?? targets[0] ?? '');
  const occNow = (code: string): number | null => {
    const col = occupancy?.get(code);
    if (col && times) return valueAt(times, col, t);
    return sim.binding.find((b) => b.target === code)?.occupancy?.central ?? null;
  };
  const mapped = sim.header.spatial_target_codes;
  const unmapped = mapped && !mapped.includes(target) ? mapped.map(TARGET_LABEL).join(', ') : null;
  return (
    <>
      {levels.length ? (
        <Section title="Levels">
          <div className="rows levels">
            {levels.map(({ label, c }) => (
              <div key={c.quantity} className="row level" data-quantity={c.quantity}>
                <span className="level-name">{label}</span>
                <Sparkline y={c.values} at={end > 0 ? t / end : null} />
                <span className="num">{`${formatValue(valueAt(times!, c.values, t))} ${c.units}`}</span>
              </div>
            ))}
          </div>
        </Section>
      ) : null}
      {targets.length ? (
        <Section
          title="Receptors"
          action={
            <span className="section-actions">
              <Button variant="quiet" className="maps-btn" aria-pressed={mapsOpen} onClick={() => setMapsOpen(!mapsOpen)}>
                All maps
              </Button>
              <Button variant="quiet" className="compare-btn" aria-pressed={compareOpen} onClick={() => setCompareOpen(!compareOpen)}>
                Compare drugs
              </Button>
            </span>
          }
        >
          {compareOpen ? createPortal(<DrugCompare onClose={closeCompare} />, document.body) : null}
          {mapsOpen ? createPortal(<DrugMaps onClose={closeMaps} />, document.body) : null}
          <ul className="receptors" role="radiogroup" aria-label="Receptors">
            {targets.map((code) => {
              const on = code === target;
              const o = occNow(code);
              return (
                <li key={code}>
                  <button type="button" role="radio" aria-checked={on} className={`receptor ${on ? 'on' : ''}`} data-target={code} onClick={() => setTraceView({ target: code })}>
                    <span className="preset-dot" aria-hidden="true" />
                    <span className="receptor-name">{TARGET_LABEL(code)}</span>
                    <span className="num">{o !== null && Number.isFinite(o) ? `${Math.round(o * 100)} %` : '–'}</span>
                  </button>
                </li>
              );
            })}
          </ul>
          <div className="row drug-show">
            <span>Show</span>
            <Segmented size="sm" label="Show on the cortex" value={show} options={SHOW_OPTIONS} onChange={setShow} />
          </div>
          {show === 'engagement' ? (
            <>
              {engagement && !unmapped ? <ColourBar name={`${TARGET_LABEL(target)} engagement`} scale={engagement.ramp} min={formatValue(engagement.range[0])} max={formatValue(engagement.range[1])} /> : null}
              {unmapped ? <p className="live-hint">{`The run maps ${unmapped} on the cortex; ${TARGET_LABEL(target)} has no map, so the cortex keeps its own colours.`}</p> : null}
            </>
          ) : (
            <>
              {mapStatus === 'on' && map ? (
                <ColourBar name={`${TARGET_LABEL(target)} density${show === 'pet' ? ', PET' : ''}`} scale="viridis" gradient={gradientCss(colormapStops('viridis'))} min={formatValue(map.range[0])} max={formatValue(map.range[1])} />
              ) : null}
              <p className="live-hint drug-show-hint" data-status={mapStatus}>
                {mapStatus === 'on' && map
                  ? show === 'pet'
                    ? `The density alone, not occupancy × density: ${TARGET_LABEL(target)} ${map.units}, the [11C]Cimbi-36 group PET (Beliveau 2017) vertex by vertex on the HD cortex (the pack’s ${map.layer} layer, ${map.finite.toLocaleString('en-GB')} of ${map.vertices.toLocaleString('en-GB')} vertices).`
                    : `The density alone, not occupancy × density: Hansen 2022’s PET parcel means on Schaefer-400, min–max scaled within the target (relative 0–1), through the HD cortex’s labels (the pack’s ${map.layer} table).`
                  : mapStatus === 'loading' || mapStatus === 'idle'
                    ? 'Reading the map…'
                    : mapWhy}
              </p>
            </>
          )}
        </Section>
      ) : null}
    </>
  );
}

const CORONAL: SectionPlane = { axis: 'coronal', mm: -12, keep: 'negative' };
const DENSITY_OPTIONS: { value: LabelDensity; label: string }[] = [
  { value: 'few', label: 'Few' },
  { value: 'normal', label: 'Normal' },
  { value: 'many', label: 'Many' },
];

/** Display (spec §5), in the screens' order: Labels, Section (with its position while a plane is
 *  on), Explode, Label density. */
function View() {
  const labelsOn = useAtlas((s) => s.labelsOn);
  const toggleLabels = useAtlas((s) => s.toggleLabels);
  const labelDensity = useAtlas((s) => s.labelDensity);
  const setLabelDensity = useAtlas((s) => s.setLabelDensity);
  const explode = useAtlas((s) => s.explode);
  const setExplode = useAtlas((s) => s.setExplode);
  const section = useAtlas((s) => s.section);
  const setSection = useAtlas((s) => s.setSection);
  const resetView = useAtlas((s) => s.resetView);
  // A connectome pack has no volume to section, and doesn't explode.
  const wiring = useAtlas((s) => isConnectome(s.catalogue));
  const plane = section[0];
  return (
    <Section title="Display" action={<IconButton size="sm" icon={RotateCcw} label="Reset view" keys={['R']} onClick={resetView} />}>
      <div className="rows">
        <label className="row">
          <span className="row-label ba-type-body">Labels</span>
          <Switch checked={labelsOn} onChange={toggleLabels} label="Labels" />
        </label>
        {wiring ? null : (
          <div className="row">
            <span className="row-label ba-type-body">Section</span>
            <span className="row-controls">
              {plane ? (
                <Segmented
                  size="sm"
                  label="Section plane"
                  value={plane.axis}
                  options={[
                    { value: 'axial', label: 'Axial' },
                    { value: 'coronal', label: 'Coronal' },
                    { value: 'sagittal', label: 'Sagittal' },
                  ]}
                  onChange={(axis) => setSection([{ ...plane, axis }])}
                />
              ) : null}
              <IconButton size="sm" icon={Scissors} label={plane ? 'Remove the section' : 'Cut a section'} active={!!plane} onClick={() => setSection(plane ? [] : [CORONAL])} />
            </span>
          </div>
        )}
        {plane && !wiring ? (
          <div className="row slider-row">
            <span className="slider-lead ba-type-small">Position</span>
            <Slider label="Section position" value={plane.mm} min={-90} max={90} step={1} onChange={(mm) => setSection([{ ...plane, mm }])} />
            {/* The flip button (icon token `flip`) at the end of the track: keep the other side of the cut. */}
            <IconButton size="sm" icon={SquareCenterlineDashedHorizontal} label="Keep the other side" onClick={() => setSection([{ ...plane, keep: plane.keep === 'positive' ? 'negative' : 'positive' }])} />
            <span className="slider-value ba-type-numeric">{`${plane.mm > 0 ? '+' : plane.mm < 0 ? '\u2212' : ''}${Math.abs(plane.mm).toFixed(0)}\u202Fmm`}</span>
          </div>
        ) : null}
        {wiring ? null : (
          <div className="row slider-row">
            <span className="slider-lead ba-type-small">Explode</span>
            <Slider label="Explode" value={explode} min={0} max={1} step={0.01} onChange={setExplode} />
            <span className="slider-value ba-type-numeric">{`${Math.round(explode * 100)}\u202F%`}</span>
          </div>
        )}
        <div className="row">
          <span className="row-label ba-type-body">Label density</span>
          <Segmented size="sm" label="Label density" value={labelDensity} options={DENSITY_OPTIONS} onChange={setLabelDensity} />
        </div>
      </div>
    </Section>
  );
}

/** A colour bar under the row that uses it (spec §5, `component.colorbar`): the quantity's name over a
 *  160 × 8 strip of its scale, and the scale's ends under it. */
/** Mind's luminance bar: design's drive ramp as the engine lays it on the cortex (the run's own layer, data/sims/layers.ts,
 *  and the live layer, features/mindLive.ts LIVE_LOOK): from the cortex's own shade, luminance the weight, nothing black
 *  (ba-look's call through the lead, 10-03). */
const LUMINANCE_BAR = gradientCss(onTissue(RAMPS.drive));

export function ColourBar({ name, scale, min, max, gradient }: { name: string; scale: string; min: string; max: string; gradient?: string }) {
  return (
    <div className="colorbar" role="img" aria-label={`${name}, ${min} to ${max}`}>
      <span className="name ba-type-small">{name}</span>
      <span className="strip" style={{ background: gradient ?? `var(--ba-scale-${scale})` }} />
      <span className="ticks ba-type-caption">
        <span>{min}</span>
        <span>{max}</span>
      </span>
    </div>
  );
}

/**
 * A dynamics run's scene sections (spec §15, Seizure): Activity, the cortex activity's switch over its
 * colour bar, and Electrodes, the run's 10–20 scalp sites and depth contacts over the picture, where
 * its forward model places them, or a real patient's own contacts lit by their recording, the onset
 * zone ringed (ElectrodeSites).
 */
function ActivitySections() {
  const main = useAtlas((s) => (s.sims.scenario === s.scenario ? s.sims.slots.find((x) => x.slot === 'main' && x.format === 'dynamics') : undefined));
  const path = main?.path;
  const toggles = useAtlas((s) => s.toggles);
  const setToggle = useAtlas((s) => s.setToggle);
  const kinds = useLoaded(() => (path ? siteKinds(path) : null), [path]);
  // A still view (Mind's Words into maps) puts nothing on the cortex: no Projection section for it.
  const still = useAtlas(stillView);
  if (!path || still) return null;
  const has = (k: SiteKind) => !!kinds.value?.includes(k);
  // Mind's run is a stimulus projected onto the cortex, not activity: the section says so (features/VisualField.tsx).
  const mind = path.startsWith('mind/');
  // A patient's contacts by the recording's modality, as the index names the run ('HUP070 · ECoG …').
  const theirs = `${patientOf(main.header as DynamicsHeader) ?? 'The patient'}’s ${modalityOf(main.annotation?.label) ?? 'SEEG'} contacts`;
  return (
    <>
      <Section title={mind ? 'Projection' : 'Activity'}>
        <div className="rows">
          <label className="row">
            <span>{mind ? 'Stimulus on the cortex' : 'Cortex activity'}</span>
            <Switch checked={toggles.activity} onChange={(on) => setToggle('activity', on)} label={mind ? 'Stimulus on the cortex' : 'Cortex activity'} />
          </label>
        </div>
        <ColourBar name={mind ? 'Luminance' : 'Activity'} scale="activity" min={mind ? 'Dark' : 'Rest'} max={mind ? 'Bright' : 'Seizure'} gradient={mind ? LUMINANCE_BAR : undefined} />
      </Section>
      {has('eeg') || has('seeg') || has('contacts') ? (
        <Section title="Electrodes">
          <div className="rows">
            {has('eeg') ? (
              <label className="row">
                <span>EEG 10–20</span>
                <Switch checked={toggles.eeg} onChange={(on) => setToggle('eeg', on)} label="EEG 10–20 sites" />
              </label>
            ) : null}
            {has('seeg') ? (
              <label className="row">
                <span>SEEG contacts</span>
                <Switch checked={toggles.seeg} onChange={(on) => setToggle('seeg', on)} label="SEEG contacts" />
              </label>
            ) : null}
            {has('contacts') ? (
              <label className="row">
                <span>{theirs}</span>
                <Switch checked={toggles.contacts} onChange={(on) => setToggle('contacts', on)} label={theirs} />
              </label>
            ) : null}
          </div>
          {has('contacts') && toggles.contacts ? (
            <p className="live-hint">
              Lit by their own recording. <span className="soz-key" aria-hidden="true" /> Ringed: in the onset zone their clinicians marked.
            </p>
          ) : null}
        </Section>
      ) : null}
    </>
  );
}

/** The scene's main cells run while the scene shows it, and its reader. */
function useCellsRun(): { path: string; sim: CellsSim } | null {
  const path = useAtlas((s) => (s.sims.scenario === s.scenario ? (s.sims.slots.find((x) => x.slot === 'main' && x.format === 'cells')?.path ?? null) : null));
  const o = path ? openedSlot('main') : undefined;
  return path && o?.format === 'cells' && o.path === path ? { path, sim: o.sim as CellsSim } : null;
}

/**
 * Neurons' Cells and Model sections (spec §15). The scene opens on one cell: Cells lists the cells it
 * can run, each on its own arbor. Model's Microcircuit switch moves it to a circuit, with the circuit (a
 * layer's microcircuit, or ACT-009's two-layer uncoupled column) and its condition, and Cells then lists
 * the circuit's cell types: each with a checkbox for its rows in the raster below, its count and its
 * rate in the run; a column's eight keep their layer in the name. Its tissue plays H01's own run, so
 * neither shows there.
 */
function NeuronSections() {
  const spec = useAtlas((s) => scenarioById(s.scenario).choices?.find((c) => c.own));
  const opts = useAtlas((s) => (s.sims.scenario === s.scenario ? s.sims.options?.[spec?.id ?? ''] : undefined));
  const value = useAtlas((s) => (s.sims.scenario === s.scenario ? s.sims.values?.[spec?.id ?? ''] : undefined));
  const onTissue = useAtlas((s) => !!s.viewPack);
  const hidden = useAtlas((s) => s.traceView.hiddenTypes);
  const setChoice = useAtlas((s) => s.setChoice);
  const setTraceView = useAtlas((s) => s.setTraceView);
  const run = useCellsRun();
  const circuit = useLoaded(() => (isCircuitKind(run?.sim.header.kind) ? loadCircuit(run!.sim) : null), [run?.sim]);
  // The cell to come back to when the switch goes off.
  const lastCell = useRef<string | null>(null);
  if (!spec || !opts || onTissue) return null;
  const available = opts.filter((o) => o.available);
  const cells = available.filter((o) => !o.circuit);
  const circuits = available.filter((o) => o.circuit);
  const current = available.find((o) => o.value === value);
  if (!current) return null;
  if (!current.circuit) lastCell.current = current.value;
  const pick = (o: { id?: string; value: string }) => setChoice(spec.id, o.id ?? o.value);
  const onCircuit = !!current.circuit;
  const names = [...new Set(circuits.map((o) => o.circuit!))];
  const conditions = onCircuit ? circuits.filter((o) => o.circuit === current.circuit) : [];
  const show = (type: string, on: boolean) => setTraceView({ hiddenTypes: on ? (hidden ?? []).filter((t) => t !== type) : [...(hidden ?? []), type] });
  const c = circuit.value;
  return (
    <>
      <Section title="Cells">
        {onCircuit ? (
          c ? (
            <div className="rows cell-types" role="list" aria-label="Cell types">
              {c.groups.map((g) => (
                <div key={g.type} className="row cell-type" role="listitem">
                  <span className="cell-type-name">
                    <Checkbox checked={!hidden?.includes(g.type)} onChange={(on) => show(g.type, on)} label={`${g.label} cells in the raster`} />
                    <span className="swatch" style={{ background: g.colour }} aria-hidden="true" />
                    {g.label}
                  </span>
                  <Tip label={`${formatCount(g.spikes)} spikes from ${formatCount(g.cells.length)} cells in the run’s ${formatValue(c.seconds, 3)} s${g.silent ? `; ${formatCount(g.silent)} never fired` : ''}`} side="right">
                    <span className="num">{`${formatCount(g.cells.length)} · ${formatValue(g.hz, 2)} Hz`}</span>
                  </Tip>
                </div>
              ))}
            </div>
          ) : null
        ) : (
          <ul className="presets" role="radiogroup" aria-label="Cells">
            {cells.map((o) => {
              const on = o.value === current.value;
              return (
                <li key={o.value}>
                  <button type="button" role="radio" aria-checked={on} className={`preset ${on ? 'on' : ''}`} onClick={() => pick(o)}>
                    <span className="preset-dot" aria-hidden="true" />
                    <span className="preset-text">
                      <span className="preset-name ba-type-body-strong">{o.label}</span>
                      {on && o.hint ? <span className="preset-hint ba-type-caption">{o.hint}</span> : null}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </Section>
      <Section title="Model">
        <div className="rows">
          {circuits.length ? (
            <label className="row">
              <span>Microcircuit</span>
              <Switch
                checked={onCircuit}
                onChange={(on) => {
                  const back = cells.find((o) => o.value === lastCell.current) ?? cells[0];
                  const to = on ? (circuits.find((o) => o.condition === 'Healthy') ?? circuits[0]) : back;
                  if (to) pick(to);
                }}
                label="Microcircuit"
              />
            </label>
          ) : null}
          {onCircuit && names.length > 1 ? (
            <div className="row choice-row">
              <span>Circuit</span>
              <Segmented
                size="sm"
                label="Circuit"
                value={current.circuit!}
                options={names.map((n) => ({ value: n, label: n, ...(circuits.find((o) => o.circuit === n)?.hint ? { hint: circuits.find((o) => o.circuit === n)!.hint! } : {}) }))}
                onChange={(n) => {
                  const to = circuits.find((o) => o.circuit === n && o.condition === current.condition) ?? circuits.find((o) => o.circuit === n);
                  if (to) pick(to);
                }}
              />
            </div>
          ) : null}
          {conditions.length > 1 ? (
            <div className="row choice-row">
              <span>Condition</span>
              <Segmented
                size="sm"
                label="Condition"
                value={current.condition!}
                options={conditions.map((o) => ({ value: o.condition!, label: o.condition!, ...(o.hint ? { hint: o.hint } : {}) }))}
                onChange={(v) => {
                  const to = conditions.find((o) => o.condition === v);
                  if (to) pick(to);
                }}
              />
            </div>
          ) : null}
        </div>
      </Section>
    </>
  );
}

/** The scene's tagline, or a chosen option's where it has one: the run the scene's own sections pick (a Neurons
 *  circuit), or a stimulation (Stimulate's tDCS). */
function taglineOf(s: AtlasState): string {
  const sc = scenarioById(s.scenario);
  // A view with its own subtitle (Lifetime's Parkinson's stages: the fetal line doesn't fit it).
  const view = sc.presets.find((p) => p.id === s.preset);
  if (view?.tagline) return view.tagline;
  if (s.sims.scenario !== sc.id) return sc.tagline;
  for (const c of sc.choices ?? []) {
    const t = s.sims.options?.[c.id]?.find((o) => o.value === s.sims.values?.[c.id])?.tagline;
    if (t) return t;
  }
  return sc.tagline;
}

export function LeftPanel() {
  const scenario = useAtlas((s) => scenarioById(s.scenario));
  const open = useAtlas((s) => s.panels.left);
  const live = useAtlas(isLive);
  const standIn = useAtlas((s) => mainSlot(s)?.standIn ?? false);
  const tagline = useAtlas(taglineOf);
  const scroll = useRef<HTMLDivElement>(null);
  return (
    <aside className={`panel left ${open ? '' : 'collapsed'}`} aria-label="Scene" data-inset="left">
      <SheetHandle label="Close the scene’s sheet" onClose={() => useAtlas.getState().togglePanel('left')} />
      <div ref={scroll} className="panel-scroll">
        <header className="scenario-head" style={{ ['--sc' as string]: `var(--sc-${scenario.id})` }}>
          <h1 className="ba-type-title">
            {scenario.name}
            {!live ? (
              <Tip label={standIn ? 'Running on an illustrative stand-in until its model’s output lands. The anatomy is real.' : 'Its simulation is still being built. The anatomy is real.'} side="right">
                <span className="preview-tag">Preview</span>
              </Tip>
            ) : null}
            <SceneTourLink />
          </h1>
          <p className="ba-type-small">{tagline}</p>
        </header>
        <Scenario />
        <Tissue />
        <Vessels />
        <Particles />
        <CfdSections />
        <DrugSections />
        <DfcSections />
        <TextSections />
        <JourneySections />
        <ActivitySections />
        <MindSections />
        <NeuronSections />
        <StimSections />
        <LifetimeSections />
        <Presets key={scenario.id} scroll={scroll} />
        <Layers />
        <View />
      </div>
    </aside>
  );
}
